import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema, AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";

const { getABogus } = require("../../utils/douyin/douyinSafe/a_bogus.js");
const { generateCsrfTokenAdvanced } = require("../../utils/douyin/douyinSafe/csrfToken.js");

// 定义参数 Schema（仅包含基础参数，无扩展参数）
export const DouyinGetMusicCategoryParamsSchema = ActionCommonParamsSchema.extend({});

// 导出参数类型
export type DouyinGetMusicCategoryParams = z.infer<typeof DouyinGetMusicCategoryParamsSchema>;

export interface Category {
    category_id: string;
    category_name: string;
    type: string;
}

export interface DouyinMusicCategoryResponse {
    status_code: number;
    status_msg: string;
    categories?: Category[];
}

export type DouyinGetMusicCategoryAction = CommonAction<
    DouyinGetMusicCategoryParams,
    {
        categorys: Category[];
    }
>;

export const douyinGetMusicCategory: DouyinGetMusicCategoryAction = async (_task, params) => {
    const userAgent =
        params.userAgent ||
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.38";

    const headers: any = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        origin: "https://creator.douyin.com",
        referer: "https://creator.douyin.com/",
        Accept: "application/json, text/plain, */*",
        "Accept-Encoding": "gzip, deflate, br, zstd",
        "Content-Type": "application/json",
        pragma: "no-cache",
        "cache-control": "no-cache",
        "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
        "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
        "bd-ticket-guard-web-version": "2",
        "sec-ch-ua-mobile": "?0",
        "user-agent": userAgent,
        "sec-fetch-site": "same-origin",
        "sec-fetch-mode": "cors",
        "sec-fetch-dest": "empty",
        "accept-language": "zh-CN,zh;q=0.9",
        priority: "u=1, i",
    };
    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.localIP, params.accountId];
    const http = new Http(...args);

    const csrfToken = generateCsrfTokenAdvanced({
        cookies: params.cookies,
        url: "https://creator.douyin.com/web/api/media/music/category",
        method: "GET",
        userAgent,
    });

    const categoryParams = {
        status: "0,2",
        count: "50",
        cursor: "0",
        source: "collection_create",
        read_aid: "2906",
        aid: "1128",
        cookie_enabled: "true",
        screen_width: params.extraParam?.browserInfo?.screen_width || "1920",
        screen_height: params.extraParam?.browserInfo?.screen_height || "1080",
        browser_language: params.extraParam?.browserInfo?.browser_language || "zh-CN",
        browser_platform: params.extraParam?.browserInfo?.browser_platform || "Win32",
        browser_name: params.extraParam?.browserInfo?.browser_name || "Mozilla",
        browser_version: userAgent,
        browser_online: "true",
        timezone_name: "Asia/Shanghai",
        support_h265: "1",
        msToken: params.cookies.find((e: any) => e.name === "msToken")?.value || "",
        a_bogus: "",
    };
    const queryParamsBefore = new URLSearchParams(categoryParams).toString();
    const aBogus = getABogus(
        `https://creator.douyin.com/web/api/media/music/category?${queryParamsBefore}`,
        {},
        userAgent,
    );
    categoryParams.a_bogus = aBogus;
    const queryString = new URLSearchParams(categoryParams).toString();
    const res = await http.api<DouyinMusicCategoryResponse>(
        {
            method: "get",
            url: `https://creator.douyin.com/web/api/media/music/category?${queryString}`,
            headers: {
                ...headers,
                "X-secsdk-csrf-token": csrfToken,
            },
        },
        { retries: 3, retryDelay: 20, timeout: 3000 },
    );
    const isSuccess = res.status_code === 0;
    const message = `抖音获取音乐分类${isSuccess ? "成功" : `失败，原因：${res.status_msg}`}${_task.debug ? ` ${http.proxyInfo}` : ""}`;
    const data: { categorys: Category[] } = isSuccess ? { categorys: res?.categories || [] } : { categorys: [] };

    return response(isSuccess ? 0 : 414, message, data);
};
